import React, {useState, useEffect, useMemo} from 'react'
import axios from 'axios'
import { API_URL } from '../../../modules/auth/core/_requests'
import {Tooltip, Popover} from 'bootstrap'
import * as XLSX from 'xlsx'

// Define the structure of the product data
interface RestockProduct {
  name: string
  brand: string
  upc: string
  sku: string
  asin: string
  image_url: string | null
  de_inventory: number
  msrp: number | null
  total_cost: number | null
  last_sold_price: number | null
  avg_price_90d?: number
  margin_dollar?: number
  margin_percent?: number
  total_sales: number
  total_amazon_inventory: number
  fulfillable_quantity: number
  daily_avg_sales: number
  days_of_supply: number
  margin_amount: number | null
  raw_restock_score: number
  normalized_score: number
  recommended_restock_qty: number
  priority: 'High' | 'Medium' | 'Low'
  // Advanced algorithm component scores
  urgency_score?: number
  profitability_score?: number
  velocity_score?: number
  risk_score?: number
  strategic_score?: number
  raw_composite_score?: number
  cost_overridden?: boolean
  account?: string
  buybox_win_percentage?: number
  buyboxAnalysis?: {
    isAvailable: boolean
    weAreContestant: boolean
    ourWinPercentage: number
    contestants: {
      name: string
      percentageWon: number
      avgPrice: number
      isFba: boolean
    }[]
    simulatedContestants?: {
      name: string
      percentageWon: number
      avgPrice: number
      minimumPrice?: number
      isFba: boolean
    }[]
  }
}

// Define the structure for our sorting configuration
type SortConfig = {
  key: keyof RestockProduct | null
  direction: 'ascending' | 'descending'
}

// Helper component for the progress bar
const DaysOfSupplyBar: React.FC<{days: number}> = ({days}) => {
  const getBarColor = () => {
    if (days < 30) return 'danger'
    if (days < 60) return 'warning'
    return 'success'
  }
  const color = getBarColor()
  const progress = Math.min((days / 90) * 100, 100) // Capped at 90 days target

  return (
    <div className='d-flex align-items-center w-100'>
      <span className='fw-bold text-gray-800 me-2' style={{minWidth: '20px'}}>{`${Math.round(
        days
      )}d`}</span>
      <div className='progress h-6px w-100'>
        <div
          className={`progress-bar bg-${color}`}
          role='progressbar'
          style={{width: `${progress}%`}}
        ></div>
      </div>
    </div>
  )
}

// Main component for the Amazon Restocking App
const AmazonRestockingWrapper: React.FC = () => {
  const [allProducts, setAllProducts] = useState<RestockProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: 'raw_restock_score', direction: 'descending'})
  const [activeFilter, setActiveFilter] = useState('all')
  const [overrideMap, setOverrideMap] = useState<Map<string, number>>(new Map())
  const [showOverridesOnly, setShowOverridesOnly] = useState(false)
  const [lastUploadName, setLastUploadName] = useState<string>('')

  // Settings state
  const [settings, setSettings] = useState({
    target_dos: 90,
    lambda: 0.05,
    price_gamma: 0.03,
    weight_urgency: 0.3,
    weight_profitability: 0.25,
    weight_velocity: 0.2,
    weight_risk: 0.15,
    weight_strategic: 0.1,
  })
  const [showSettings, setShowSettings] = useState(false)

  const handleSettingChange = (key: string, value: number) => {
    setSettings((prev) => ({...prev, [key]: value}))
  }

  const startAnalysis = async () => {
    setAllProducts([])
    setError(null)
    setLoading(true)
    
    try {
      const response = await axios.post(`${API_URL}/api/de-apps/amazon-restocking`, {settings})
      setAllProducts(response.data.products)
      setLastUpdated(new Date())
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start analysis.')
    } finally {
      setLoading(false)
    }
  }

  // Filter products based on active filter
  const filteredProducts = useMemo(() => {
    // If override-only mode, ignore day-of-supply filters and show all overridden rows
    if (showOverridesOnly) {
      return allProducts.filter((p) => p.cost_overridden)
    }

    if (activeFilter === 'all') return allProducts

    return allProducts.filter((p) => {
      const days = p.days_of_supply
      if (activeFilter === 'outOfStock') return days < 1
      if (activeFilter === 'urgent') return days >= 1 && days <= 15
      if (activeFilter === 'high') return days > 15 && days <= 30
      if (activeFilter === 'medium') return days > 30 && days <= 60
      return true
    })
  }, [allProducts, activeFilter, showOverridesOnly])

  // Memoized sorting logic
  const sortedProducts = useMemo(() => {
    const sortableProducts = [...filteredProducts]
    if (sortConfig.key) {
      sortableProducts.sort((a, b) => {
        const aValue = a[sortConfig.key!]
        const bValue = b[sortConfig.key!]

        if (aValue === null || aValue === undefined) return 1
        if (bValue === null || bValue === undefined) return -1

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1
        }
        return 0
      })
    }
    return sortableProducts
  }, [filteredProducts, sortConfig])

  // Initialize popovers
  useEffect(() => {
    const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'))
    const popoverList = popoverTriggerList.map(function (popoverTriggerEl) {
      const element = popoverTriggerEl as HTMLElement
      const pop = new Popover(element, {
        html: true,
        sanitize: false,
        customClass: 'wide-popover',
      })
      // Attach close-button injection on show
      element.addEventListener('shown.bs.popover', () => {
        // latest popover element
        const popoverElems = document.querySelectorAll('.popover');
        const popEl = popoverElems[popoverElems.length-1] as HTMLElement | undefined;
        if(popEl && !popEl.querySelector('.btn-close')) {
          const btn = document.createElement('button');
          btn.className = 'btn-close position-absolute top-0 end-0 m-2';
          btn.addEventListener('click', () => {
            pop.hide();
          });
          popEl.appendChild(btn);
        }
      })
      return pop
    })
    // Cleanup function
    return () => {
      popoverList.forEach(p => p.dispose())
    }
  }, [sortedProducts, showSettings]) // Re-initialize when data or modal changes

  // Function to handle sort requests
  const requestSort = (key: keyof RestockProduct) => {
    let direction: 'ascending' | 'descending' = 'ascending'
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending'
    }
    setSortConfig({key, direction})
  }

  // Summary metrics
  const summary = useMemo(() => {
    return {
      totalProducts: filteredProducts.length,
      highPriority: filteredProducts.filter((p) => p.priority === 'High').length,
      totalUnits: filteredProducts.reduce((sum, p) => sum + p.recommended_restock_qty, 0),
    }
  }, [filteredProducts])

  const handleFilterChange = (filterKey: string) => {
    setActiveFilter(filterKey)
    }

  // Export current table (sorted & filtered) to Excel
  const exportToExcel = () => {
    // Prepare data
    const dataForExport = sortedProducts.map((p) => ({
      Name: p.name,
      Brand: p.brand,
      SKU: p.sku,
      ASIN: p.asin,
      'Days of Supply': Math.round(p.days_of_supply),
      'Recommended Qty': p.recommended_restock_qty,
      'Composite Score': (p.raw_composite_score || 0).toFixed(1),
      'Urgency Score': (p.urgency_score || 0).toFixed(1),
      'Profitability Score': (p.profitability_score || 0).toFixed(1),
      'Velocity Score': (p.velocity_score || 0).toFixed(1),
      'Risk Score': (p.risk_score || 0).toFixed(1),
      'Strategic Score': (p.strategic_score || 0).toFixed(1),
      'Margin %': p.margin_amount ?? 0,
      'Daily Avg Sales': p.daily_avg_sales.toFixed(2),
      'Total Amazon Inventory': p.total_amazon_inventory,
      Priority: p.priority,
    }))

    const ws = XLSX.utils.json_to_sheet(dataForExport)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'RestockAdvisor')
    const fileName = `restock_advisor_${new Date().toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(wb, fileName)
  }

  // Handle Excel upload
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLastUploadName(file.name)
    try {
      const data = await file.arrayBuffer()
      const workbook = XLSX.read(data)
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows: any[][] = XLSX.utils.sheet_to_json(firstSheet, {header: 1}) as any[][]
      if (rows.length === 0) return
      // Detect header row indexes for asin & cost (case-insensitive)
      const headerRow = rows[0].map((h: any) => (h ? h.toString().toLowerCase().trim() : ''))
      let asinIdx = headerRow.findIndex((h: string) => h === 'asin')
      let costIdx = headerRow.findIndex((h: string) => h === 'cost')
      // If headers not found, assume first two columns
      if (asinIdx === -1) asinIdx = 0
      if (costIdx === -1) costIdx = 1
      const newMap = new Map(overrideMap)
      rows.slice(1).forEach((row) => {
        const asinVal = row[asinIdx]
        const costVal = row[costIdx]
        if (!asinVal) return
        const asin = asinVal.toString().trim()
        if (!asin) return
        if (costVal === undefined || costVal === null || costVal === '') return
        // Parse cost (strip currency symbols, commas)
        const costNum = parseFloat(costVal.toString().replace(/[^0-9.\-]/g, ''))
        if (isNaN(costNum)) return
        newMap.set(asin, costNum)
      })
      setOverrideMap(newMap)

      // Call backend to recompute scores with overrides
      try {
        setLoading(true)
        const overridesArr = Array.from(newMap.entries()).map(([asin, cost]) => ({asin, cost}))
        const response = await axios.post(`${API_URL}/api/de-apps/amazon-restocking`, {overrides: overridesArr, settings})
        setAllProducts(response.data.products)
        setLastUpdated(new Date())
      } catch (err) {
        console.error('Error recomputing with overrides:', err)
      } finally {
        setLoading(false)
      }

      // Clear file input value so same file can be re-selected if needed
      e.target.value = ''
    } catch (err) {
      console.error('Error parsing Excel:', err)
    }
  }

  // Render a loading spinner
  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  // Render an error message
  if (error) {
    return (
      <div className='card bg-body'>
        <div className='card-body'>
          <div className='alert alert-danger'>{error}</div>
        </div>
      </div>
    )
  }

  const getSortIcon = (key: keyof RestockProduct) => {
    if (sortConfig.key !== key) {
      return <i className='fa-solid fa-sort text-muted'></i>
    }
    if (sortConfig.direction === 'ascending') {
      return <i className='fa-solid fa-sort-up'></i>
    }
    return <i className='fa-solid fa-sort-down'></i>
  }

  const filterButtons = [
    { key: 'all', label: 'All' },
    { key: 'outOfStock', label: 'Out of Stock' },
    { key: 'urgent', 'label': '1-15 Days Left' },
    { key: 'high', 'label': '16-30 Days Left' },
    { key: 'medium', 'label': '31-60 Days Left' },
  ];

  // Main component render
  return (
    <>
    <style>{`
      .popover.wide-popover {
        width: 400px;
      }
      .product-image-container {
        width: 50px;
        height: 50px;
        background-color: white;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }
      .product-image-container img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .priority-badge {
        width: 100px;
        text-align: center;
        border: 1px solid rgba(0,0,0,0.1);
      }
      .label-with-padding {
        margin-top: 3px;
      }
      [data-bs-toggle="popover"] {
        cursor: pointer;
      }
    `}</style>
    <div className='card shadow-sm bg-body text-body'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='fw-bold'>Amazon Restocking Advisor</h3>
          <span
            className='ms-4'
            data-bs-toggle='popover'
            data-bs-trigger='click'
            data-bs-placement='right'
            data-bs-title='<div class="fw-bold fs-5 text-dark">Advanced Multi-Factor Weighted Scoring Algorithm</div>'
            data-bs-content='
              <div class="text-start">
                <p class="mb-2 fw-bold">Composite Score = Σ(w<sub>i</sub> × S<sub>i</sub>)</p>
                <div class="my-2 p-2 bg-light-dark rounded-2">
                  <code class="d-block">S = 0.30×U + 0.25×P + 0.20×V + 0.15×R + 0.10×St</code>
                </div>
                <div class="mb-2">
                  <strong>1. Urgency Score (30%):</strong><br/>
                  U(t) = e<sup>-λt</sup> × E<sub>m</sub> + R<sub>stockout</sub><br/>
                  <small>where λ=0.05, E<sub>m</sub>=emergency multiplier</small>
                </div>
                <div class="mb-2">
                  <strong>2. Profitability Score (25%):</strong><br/>
                  P(m,v) = M<sub>norm</sub> + V<sub>weight</sub> + ROI<br/>
                  <small>Normalized margin × volume factor × return on investment</small>
                </div>
                <div class="mb-2">
                  <strong>3. Velocity Score (20%):</strong><br/>
                  V(s,σ) = Z<sub>sales</sub> + M<sub>position</sub><br/>
                  <small>Sales momentum (Z-score) + market positioning</small>
                </div>
                <div class="mb-2">
                  <strong>4. Risk Score (15%):</strong><br/>
                  R(σ,τ) = (O<sub>risk</sub> + D<sub>stability</sub>) / 2<br/>
                  <small>Overstock risk assessment + demand stability</small>
                </div>
                <div class="mb-0">
                  <strong>5. Strategic Score (10%):</strong><br/>
                  St = B<sub>volume</sub> + B<sub>margin</sub> + E<sub>efficiency</sub><br/>
                  <small>Business priority bonuses + inventory efficiency</small>
                </div>
              </div>
            '
          >
            <i className='fa-solid fa-circle-question text-primary fs-4'></i>
          </span>
        </div>
        <div className='card-toolbar w-100'>
          <div className='d-flex align-items-center justify-content-end w-100 gap-2 flex-wrap'>
            <div className='text-muted fs-7 me-auto'>
              {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString()}`}
            </div>

            <input type='file' accept='.xlsx,.xls' id='excelUpload' style={{display:'none'}} onChange={handleExcelUpload} />
            <div className='btn-group'>
              <button className='btn btn-sm btn-light-primary' onClick={() => document.getElementById('excelUpload')?.click()}>
                <i className='fa-solid fa-file-import me-1'></i>Excel + Run
              </button>
              <button className='btn btn-sm btn-light-primary' onClick={startAnalysis} disabled={loading}>
                <i className='fa-solid fa-play me-1'></i>Run Only
              </button>
              <span
                className='ms-1'
                data-bs-toggle='popover'
                data-bs-trigger='click'
                data-bs-placement='bottom'
                data-bs-title={
                  `<div class="fw-bold fs-6 text-dark">Excel Upload Format</div>`
                }
                data-bs-content={
                  `<div class="text-start">
                     <p class="mb-1">Prepare a spreadsheet with two header cells:</p>
                     <ul class="mb-1 ps-3">
                       <li><code>asin</code> – the 10-character Amazon ASIN</li>
                       <li><code>cost</code> – numeric cost per unit (no commas)</li>
                     </ul>
                     <p class="mb-0">Column order doesn't matter; headers are matched case-insensitively.</p>
                   </div>`
                }
              >
                <i className='fa-solid fa-circle-question text-primary'></i>
              </span>
            </div>

            {lastUploadName && (
              <span className='text-muted fs-8 text-truncate' style={{maxWidth: '150px'}} title={lastUploadName}>
                {lastUploadName}
              </span>
            )}

            <button className='btn btn-sm btn-light-secondary' onClick={() => setShowOverridesOnly(!showOverridesOnly)} disabled={allProducts.length===0}>
              {showOverridesOnly ? 'Show All' : 'Overrides Only'}
            </button>

            <button className='btn btn-sm btn-success' onClick={exportToExcel} disabled={sortedProducts.length === 0}>
              <i className='fa-solid fa-file-excel me-1'></i>Export
            </button>
            <button className='btn btn-sm btn-light-info' onClick={()=>setShowSettings(true)}>
              <i className='fa-solid fa-sliders me-1'></i>Customize
            </button>
          </div>
        </div>
      </div>

      {!loading && allProducts.length === 0 && (
         <div className='d-flex flex-column align-items-center justify-content-center p-10'>
            <i className='fa-solid fa-play-circle text-primary fs-3x mb-4'></i>
            <p className='fs-5 text-gray-700'>Click "Start Analysis" to begin.</p>
         </div>
      )}

      {allProducts.length > 0 && (
        <>
          {/* Filters */}
          <div className='d-flex flex-wrap justify-content-start align-items-center px-9 pt-3 pb-5'>
            <span className='fs-6 fw-bold text-gray-700 me-4'>Filter by Days of Supply:</span>
            <div className='d-flex flex-wrap gap-2'>
              {filterButtons.map((btn) => (
                <button
                  key={btn.key}
                  className={`btn btn-sm btn-outline btn-outline-primary ${
                    activeFilter === btn.key ? 'active' : ''
                  }`}
                  onClick={() => handleFilterChange(btn.key)}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          <div className='card-body pt-0'>
            {/* Summary Cards */}
            <div className='row g-5 g-xl-8 mb-8'>
              <div className='col-xl-4'>
                <div className='card bg-light-primary hoverable card-xl-stretch'>
                  <div className='card-body'>
                    <i className='fa-solid fa-boxes-packing text-primary fs-3x ms-n1'></i>
                    <div className='text-primary fw-bold fs-2 mb-2 mt-5'>{summary.totalProducts}</div>
                    <div className='fw-semibold text-primary'>Products to Restock</div>
                  </div>
                </div>
              </div>
              <div className='col-xl-4'>
                <div className='card bg-light-danger hoverable card-xl-stretch'>
                  <div className='card-body'>
                    <i className='fa-solid fa-triangle-exclamation text-danger fs-3x ms-n1'></i>
                    <div className='text-danger fw-bold fs-2 mb-2 mt-5'>{summary.highPriority}</div>
                    <div className='fw-semibold text-danger'>High Priority Items</div>
                  </div>
                </div>
              </div>
              <div className='col-xl-4'>
                <div className='card bg-light-success hoverable card-xl-stretch'>
                  <div className='card-body'>
                    <i className='fa-solid fa-box-open text-success fs-3x ms-n1'></i>
                    <div className='text-success fw-bold fs-2 mb-2 mt-5'>
                      {summary.totalUnits.toLocaleString()}
                    </div>
                    <div className='fw-semibold text-success'>Total Units Recommended</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='min-w-250px'>Product</th>
                    <th
                      className='min-w-150px cursor-pointer'
                      onClick={() => requestSort('normalized_score')}
                    >
                      Restock Score {getSortIcon('normalized_score')}
                    </th>
                    <th
                      className='min-w-150px cursor-pointer'
                      onClick={() => requestSort('buybox_win_percentage')}
                    >
                      Buybox Win % {getSortIcon('buybox_win_percentage')}
                    </th>
                    <th
                      className='min-w-200px cursor-pointer'
                      onClick={() => requestSort('days_of_supply')}
                    >
                      Amz Inventory & Sales {getSortIcon('days_of_supply')}
                    </th>
                    <th
                      className='min-w-250px cursor-pointer'
                      onClick={() => requestSort('recommended_restock_qty')}
                    >
                      Recommendation & Financials
                    </th>
                    <th className='min-w-150px'>Pricing</th>
                    <th className='min-w-150px'>Current Revenue</th>
                    <th className='min-w-150px'>Current Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProducts.map((p, index) => {
                    const ourSellerNames = ['ECAMZ SALES', 'Vintage Charm Shop']
                    let isBuyboxWinner = false
                    if (p.buyboxAnalysis?.isAvailable) {
                      if (p.buyboxAnalysis.simulatedContestants?.length) {
                        // Simulation case: check if we are #1 in the simulated list
                        if (ourSellerNames.includes(p.buyboxAnalysis.simulatedContestants[0].name)) {
                          isBuyboxWinner = true
                        }
                      } else if (p.buyboxAnalysis.contestants?.length) {
                        // Actual case: check if we are #1 in the current list
                        if (ourSellerNames.includes(p.buyboxAnalysis.contestants[0].name)) {
                          isBuyboxWinner = true
                        }
                      }
                    }

                    return (
                    <tr key={`${p.asin}-${p.sku}-${index}`}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='product-image-container me-5'>
                            <img
                              src={`https://` + p.image_url}
                              alt={p.name}
                              onError={(e) =>
                                (e.currentTarget.src = './media/icons/custom/sunglasses-icon.png')
                              }
                            />
                          </div>
                          <div className='d-flex justify-content-start flex-column'>
                            <a href={`https://amazon.com/dp/${p.asin}`} target='_blank' rel='noopener noreferrer' className='text-dark fw-bold text-hover-primary fs-6'>
                              {p.name}
                            </a>
                            <span className='text-muted fw-semibold d-block fs-7'>
                              ASIN: {p.asin} {p.account && (
                                <span className={`badge ms-1 ${p.account==='ECAMZ' ? 'badge-light-info' : 'badge-light-warning'}`}>{p.account}</span>
                              )}
                            </span>
                            <span className='text-muted fw-semibold d-block fs-7'>
                              SKU: {p.sku}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div
                          data-bs-toggle='popover'
                          data-bs-trigger='click'
                          data-bs-placement='top'
                          data-bs-title='<div class="fw-bold fs-5 text-dark">Advanced Score Breakdown</div>'
                          data-bs-content={`
                        <div class="d-flex flex-column text-start">
                          <div class="d-flex justify-content-between mb-1">
                            <span class="fw-semibold text-muted">Urgency Score (30%):</span>
                            <span class="fw-bold text-gray-800">${(p.urgency_score || 0).toFixed(1)}/100</span>
                          </div>
                          <div class="d-flex justify-content-between mb-1">
                            <span class="fw-semibold text-muted">Profitability (25%):</span>
                            <span class="fw-bold text-gray-800">${(p.profitability_score || 0).toFixed(1)}/100</span>
                          </div>
                          <div class="d-flex justify-content-between mb-1">
                            <span class="fw-semibold text-muted">Velocity (20%):</span>
                            <span class="fw-bold text-gray-800">${(p.velocity_score || 0).toFixed(1)}/100</span>
                          </div>
                          <div class="d-flex justify-content-between mb-1">
                            <span class="fw-semibold text-muted">Risk Score (15%):</span>
                            <span class="fw-bold text-gray-800">${(p.risk_score || 0).toFixed(1)}/100</span>
                          </div>
                          <div class="d-flex justify-content-between mb-1">
                            <span class="fw-semibold text-muted">Strategic (10%):</span>
                            <span class="fw-bold text-gray-800">${(p.strategic_score || 0).toFixed(1)}/100</span>
                          </div>
                          <div class="separator my-2"></div>
                           <div class="d-flex justify-content-between mb-1">
                            <span class="fw-semibold text-muted">Composite Score:</span>
                            <span class="fw-bold text-gray-800">${(p.raw_composite_score || 0).toFixed(1)}/100</span>
                          </div>
                           <div class="d-flex justify-content-between">
                            <span class="fw-semibold text-muted">Days of Supply:</span>
                            <span class="fw-bold text-gray-800">${Math.round(p.days_of_supply)} days</span>
                          </div>
                        </div>
                      `}
                        >
                          <span className='text-primary fw-bolder d-block fs-4'>
                            {p.normalized_score.toFixed(1)} / 10
                          </span>
                        </div>
                      </td>
                      <td>
                        <div
                          data-bs-toggle='popover'
                          data-bs-trigger='click'
                          data-bs-placement='top'
                          data-bs-title='<div class="fw-bold fs-5 text-dark">Buybox Analysis</div>'
                          data-bs-content={`
                            ${
                              p.buyboxAnalysis?.isAvailable
                                ? `
                                  <div class="text-start">
                                    <p class="fw-bold mb-1">Current Contestants</p>
                                    <ul class="list-unstyled mb-2">
                                      ${p.buyboxAnalysis.contestants
                                        .map(
                                          (c) =>
                                            `<li class="d-flex align-items-center ${
                                              ourSellerNames.includes(c.name) ? 'text-primary' : ''
                                            }">
                                              <span class="fw-bold me-2">${c.percentageWon.toFixed(
                                                2
                                              )}%</span>
                                              <span class="${
                                                ourSellerNames.includes(c.name)
                                                  ? 'fw-bolder'
                                                  : 'text-muted'
                                              }">${c.name}</span>
                                              <span class="fw-bold text-gray-800 ms-auto">$${c.avgPrice?.toFixed(2)}</span>
                                            </li>`
                                        )
                                        .join('')}
                                    </ul>
                                    ${
                                      p.buyboxAnalysis.simulatedContestants
                                        ? `
                                      <p class="fw-bold mb-1 mt-3">Simulated Contestants (with us)</p>
                                      <ul class="list-unstyled mb-0">
                                        ${p.buyboxAnalysis.simulatedContestants
                                          .map(
                                            (c) =>
                                              `<li class="d-flex align-items-center ${
                                                ourSellerNames.includes(c.name)
                                                  ? 'text-primary'
                                                  : ''
                                              }">
                                                <span class="fw-bold me-2">${c.percentageWon.toFixed(
                                                  2
                                                )}%</span>
                                                <span class="fw-bolder">${c.name}</span>
                                                <span class="fw-bold text-gray-800 ms-auto">
                                                ${
                                                  ourSellerNames.includes(c.name) &&
                                                  c.minimumPrice &&
                                                  c.minimumPrice.toFixed(2) !== c.avgPrice.toFixed(2)
                                                    ? `$${c.minimumPrice.toFixed(2)} - $${c.avgPrice.toFixed(2)}`
                                                    : `$${c.avgPrice?.toFixed(2)}`
                                                }
                                                </span>
                                              </li>`
                                          )
                                          .join('')}
                                      </ul>
                                      `
                                        : ''
                                    }
                                  </div>
                                `
                                : '<div class="text-start text-muted">No recent Buy Box data available for this ASIN.</div>'
                            }
                          `}
                        >
                          {p.buyboxAnalysis?.isAvailable ? (
                            <>
                              <span className='text-primary fw-bolder d-block fs-4'>
                                {(p.buybox_win_percentage || 0).toFixed(2)}%
                                {isBuyboxWinner && <i className='fa-solid fa-trophy text-warning ms-2'></i>}
                              </span>
                              <span
                                className={`fw-semibold d-block fs-7 ${
                                  p.buyboxAnalysis?.weAreContestant ? 'text-success' : 'text-info'
                                }`}
                              >
                                {p.buyboxAnalysis?.weAreContestant ? 'Actual' : 'Simulated'}
                              </span>
                            </>
                          ) : (
                            <span className='text-muted fw-bold d-block fs-6'>N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                           <div
                              data-bs-toggle='popover'
                              data-bs-trigger='click'
                              data-bs-placement='top'
                              data-bs-title='<div class="fw-bold fs-5 text-dark">Sales Velocity Analysis</div>'
                              data-bs-content={`
                                <div class="text-start">
                                  <p class="mb-1 fw-bold">λ<sub>daily</sub> = Σ(Q<sub>i</sub>) / Δt</p>
                                  <code class="d-block p-2 bg-light-dark rounded-2">Daily Velocity = Total Sales / 90 Days</code>
                                  <code class="d-block p-2 bg-light-dark rounded-2 mt-1">${p.total_sales} units / 90 days = ${p.daily_avg_sales.toFixed(2)} u/d</code>
                                  <small class="mt-2 d-block text-muted">Used in velocity scoring with Z-score normalization:<br/>Z = (x - μ) / σ</small>
                                </div>
                              `}
                            >
                              <span className='text-dark fw-bold d-block fs-6'>
                                {p.daily_avg_sales.toFixed(2)} units/day
                              </span>
                            </div>
                          <DaysOfSupplyBar days={p.days_of_supply} />
                          <span className='text-muted fw-semibold d-block fs-7 mt-1'>
                            {p.total_amazon_inventory.toLocaleString()} units total
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='text-dark fw-bold d-block fs-6'>
                            {p.recommended_restock_qty === 0 ? (
                              <span className='badge badge-light-success'>Good</span>
                            ) : (
                              <>Restock {p.recommended_restock_qty.toLocaleString()} units</>
                            )}
                          </span>
                          {p.priority === 'High' && (
                            <span className='badge badge-light-danger fs-7 fw-bold mt-1 priority-badge'>
                              High Priority
                            </span>
                          )}
                          {p.priority === 'Medium' && (
                            <span className='badge badge-light-warning fs-7 fw-bold mt-1 priority-badge'>
                              Medium Priority
                            </span>
                          )}
                          {p.priority === 'Low' && (
                            <span className='badge badge-light-info fs-7 fw-bold mt-1 priority-badge'>
                              Low Priority
                            </span>
                          )}
                        </div>
                        <div className='d-flex justify-content-start gap-4 mt-2'>
                           <div
                            data-bs-toggle='popover'
                            data-bs-trigger='click'
                            data-bs-placement='top'
                            data-bs-title='<div class="fw-bold fs-5 text-dark">Est. Revenue</div>'
                            data-bs-content={`
                              <div class="text-start">
                                <code class="d-block p-2 bg-light-dark rounded-2">Last Sold Price × Rec. Units</code>
                                <code class="d-block p-2 bg-light-dark rounded-2 mt-1">$${(p.last_sold_price || 0).toFixed(2)} × ${p.recommended_restock_qty}</code>
                              </div>
                            `}
                          >
                            <span className='text-success fw-bold d-block fs-6'>
                              ${(
                                (p.last_sold_price || 0) * p.recommended_restock_qty
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                             <span className='text-muted fw-semibold d-block fs-7 label-with-padding'>Est. Revenue</span>
                          </div>
                          <div
                            data-bs-toggle='popover'
                            data-bs-trigger='click'
                            data-bs-placement='top'
                            data-bs-title='<div class="fw-bold fs-5 text-dark">Est. Cost</div>'
                            data-bs-content={`
                              <div class="text-start">
                                <code class="d-block p-2 bg-light-dark rounded-2">Product Cost × Rec. Units</code>
                                <code class="d-block p-2 bg-light-dark rounded-2 mt-1">$${(p.total_cost || 0).toFixed(2)} × ${p.recommended_restock_qty}</code>
                              </div>
                            `}
                          >
                            <span className='text-warning fw-bold d-block fs-6'>
                              ${(
                                (p.total_cost || 0) * p.recommended_restock_qty
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              {p.cost_overridden && <span className='badge badge-light-primary ms-1'>OVR</span>}
                            </span>
                             <span className='text-muted fw-semibold d-block fs-7 label-with-padding'>Est. Cost</span>
                          </div>
                        </div>
                      </td>
                      <td>
                         <div className='d-flex flex-column'>
                            <div
                              data-bs-toggle='popover'
                              data-bs-trigger='click'
                              data-bs-placement='top'
                              data-bs-title='<div class="fw-bold fs-6 text-dark">90-Day Recency-Weighted Price</div>'
                              data-bs-content={`<div class='text-start'>Weighted average using e^{-γΔt} with γ=${settings.price_gamma}.<br/>Older orders contribute less.</div>`}
                            >
                              <span className='text-dark fw-bold d-block fs-6'>
                                ${ (p.avg_price_90d ?? 0).toFixed(2) }
                              </span>
                            </div>
                            <span className='text-muted fw-semibold d-block fs-7'>Avg 90d</span>
                            <div className='d-flex flex-column mt-1'>
                              <span className='text-dark fw-bold d-block fs-6'>${(p.total_cost || 0).toFixed(2)}</span>
                              <span className='text-muted fw-semibold d-block fs-7'>Cost</span>
                            </div>
                            <div className='d-flex flex-column mt-1'>
                              <span className='text-dark fw-bold d-block fs-6'>
                                <span className='text-dark fw-bold d-block fs-6'>
                                  ${ ((p.avg_price_90d ?? 0) - (p.total_cost ?? 0)).toFixed(2) }
                                  <span className='text-muted'> ({ ((p.total_cost ?? 0) > 0 ? (((p.avg_price_90d ?? 0) - (p.total_cost ?? 0)) / (p.total_cost ?? 1) * 100) : 0).toFixed(1) }%)</span>
                                </span>
                              </span>
                            </div>
                          </div>
                          </td>
                      <td>
                        <div
                          data-bs-toggle='popover'
                          data-bs-trigger='click'
                          data-bs-placement='top'
                          data-bs-title='<div class="fw-bold fs-5 text-dark">Est. Revenue of Current Inventory</div>'
                          data-bs-content={`
                            <div class="text-start">
                              <code class="d-block p-2 bg-light-dark rounded-2">Last Sold Price × Current FBA Qty</code>
                              <code class="d-block p-2 bg-light-dark rounded-2 mt-1">$${(p.last_sold_price || 0).toFixed(2)} × ${p.total_amazon_inventory}</code>
                            </div>
                          `}
                        >
                          <span className='text-success fw-bold d-block fs-6'>
                            $
                            {(
                              (p.last_sold_price || 0) * p.total_amazon_inventory
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div
                          data-bs-toggle='popover'
                          data-bs-trigger='click'
                          data-bs-placement='top'
                          data-bs-title='<div class="fw-bold fs-5 text-dark">Est. Cost of Current Inventory</div>'
                          data-bs-content={`
                            <div class="text-start">
                              <code class="d-block p-2 bg-light-dark rounded-2">Product Cost × Current FBA Qty</code>
                              <code class="d-block p-2 bg-light-dark rounded-2 mt-1">$${(p.total_cost || 0).toFixed(2)} × ${p.total_amazon_inventory}</code>
                            </div>
                          `}
                        >
                          <span className='text-warning fw-bold d-block fs-6'>
                            $
                            {(
                              (p.total_cost || 0) * p.total_amazon_inventory
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
    {showSettings && (
      <div className='modal fade show d-block' style={{background:'rgba(0,0,0,0.5)'}}>
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Customize Algorithm</h5>
              <button type='button' className='btn-close' onClick={()=>setShowSettings(false)}></button>
            </div>
            <div className='modal-body'>
              <div className='row g-3'>
                <div className='col-md-4'>
                  <label className='form-label'>Target Days of Supply</label>
                  <input type='number' className='form-control' value={settings.target_dos} min={30} max={180} onChange={(e)=>handleSettingChange('target_dos', Number(e.target.value))}/>
                </div>
                <div className='col-md-4'>
                  <label className='form-label'>Urgency Decay λ
                    <span
                      className='ms-1'
                      data-bs-toggle='popover'
                      data-bs-trigger='click'
                      data-bs-placement='right'
                      data-bs-title={`<div class="fw-bold fs-6 text-dark">What is λ?</div>`}
                      data-bs-content={`<div class='text-start'>λ (lambda) controls the <em>exponential decay</em> in the urgency formula:<br/><code>U(t)=e^{-λt}</code>.<br/>• Higher λ ⇒ urgency drops faster as Days&nbsp;of&nbsp;Supply increases.<br/>• Lower λ ⇒ urgency decays slower.<br/>Default 0.05 ≈&nbsp;5% decay per day.</div>`}
                    >
                      <i className='fa-solid fa-circle-question text-primary'></i>
                    </span>
                  </label>
                  <input type='number' step='0.01' className='form-control' value={settings.lambda} onChange={(e)=>handleSettingChange('lambda', Number(e.target.value))}/>
                </div>
              </div>
              <hr/>
              <h6>Weights (must sum to 1.0)</h6>
              {(() => {
                const descriptions: Record<string,string> = {
                  urgency: `<div class='text-start'>
                    <p class='fw-bold mb-1'>Urgency term&nbsp;<code>S_U</code></p>
                    <pre class='mb-1' style='white-space:pre-wrap;font-size:90%;'>S_U = w_U · e^{-λ·t}</pre>
                    <pre class='mb-0' style='font-size:85%;'>t = Days&nbsp;of&nbsp;Supply (days)
λ = Decay constant (${settings.lambda})
w_U = Slider weight 0–1</pre>
                  </div>`,
                  profitability: `<div class='text-start'>
                    <p class='fw-bold mb-1'>Profitability term&nbsp;<code>S_P</code></p>
                    <pre class='mb-1' style='white-space:pre-wrap;font-size:90%;'>S_P = w_P · (M_norm + V_vol + ROI)</pre>
                    <pre class='mb-0' style='font-size:85%;'>M_norm = Normalised margin %
V_vol  = Volume weight
ROI    = (price-cost)/cost
w_P    = Slider weight 0–1</pre>
                  </div>`,
                  velocity: `<div class='text-start'>
                    <p class='fw-bold mb-1'>Velocity term&nbsp;<code>S_V</code></p>
                    <pre class='mb-1' style='white-space:pre-wrap;font-size:90%;'>S_V = w_V · (Z_sales + M_pos)</pre>
                    <pre class='mb-0' style='font-size:85%;'>Z_sales = Z-score of sales
M_pos   = Price vs MSRP
w_V     = Slider weight 0–1</pre>
                  </div>`,
                  risk: `<div class='text-start'>
                    <p class='fw-bold mb-1'>Risk term&nbsp;<code>S_R</code></p>
                    <pre class='mb-1' style='white-space:pre-wrap;font-size:90%;'>S_R = w_R · (O_risk + D_stab)/2</pre>
                    <pre class='mb-0' style='font-size:85%;'>O_risk = Overstock risk score
D_stab = Demand stability score
w_R    = Slider weight 0–1</pre>
                  </div>`,
                  strategic: `<div class='text-start'>
                    <p class='fw-bold mb-1'>Strategic term&nbsp;<code>S_St</code></p>
                    <pre class='mb-1' style='white-space:pre-wrap;font-size:90%;'>S_St = w_St · (B_vol + B_mar + E_eff)</pre>
                    <pre class='mb-0' style='font-size:85%;'>B_vol = Volume bonus
B_mar = Margin bonus
E_eff = Efficiency bonus
w_St  = Slider weight 0–1</pre>
                  </div>`
                }
                return ['urgency','profitability','velocity','risk','strategic'].map((w)=> (
                  <div className='mb-3' key={w}>
                    <label className='form-label text-capitalize'>
                      {w} ({(settings[`weight_${w}` as keyof typeof settings]*100).toFixed(0)}%)
                      <span
                        className='ms-1'
                        data-bs-toggle='popover'
                        data-bs-trigger='click'
                        data-bs-placement='right'
                        data-bs-title='<div class="fw-bold fs-6 text-dark">Weight Explanation</div>'
                        data-bs-content={descriptions[w]}
                      >
                        <i className='fa-solid fa-circle-question text-primary'></i>
                      </span>
                    </label>
                    <input type='range' min='0' max='1' step='0.05' className='form-range' value={settings[`weight_${w}` as keyof typeof settings]} onChange={(e)=>handleSettingChange(`weight_${w}`, Number(e.target.value))}/>
                  </div>
                ))
              })()}
              <div className='alert alert-info'>Current total: {(settings.weight_urgency+settings.weight_profitability+settings.weight_velocity+settings.weight_risk+settings.weight_strategic).toFixed(2)}</div>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={()=>setShowSettings(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  )
}

export default AmazonRestockingWrapper
